import React from 'react'
import { useAuth } from '@/contexts/AuthContext'

export default function DashboardPage() {
  const { user } = useAuth()

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground">
            Welcome back, {user?.email?.split('@')[0]}
          </h1>
          <p className="text-muted-foreground mt-2">
            Your personal trading dashboard
          </p>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Watchlist Items</p>
                <p className="text-2xl font-bold text-foreground">12</p>
              </div>
              <div className="text-primary-600">
                📊
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Saved Layouts</p>
                <p className="text-2xl font-bold text-foreground">3</p>
              </div>
              <div className="text-primary-600">
                💾
              </div>
            </div>
          </div>

          <div className="card p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Charts Viewed</p>
                <p className="text-2xl font-bold text-foreground">47</p>
              </div>
              <div className="text-primary-600">
                📈
              </div>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Watchlist */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Your Watchlist</h3>
              <p className="card-description">
                Your most watched trading pairs
              </p>
            </div>
            <div className="card-content">
              <div className="space-y-3">
                {[
                  { symbol: 'EURUSD', name: 'Euro / US Dollar', change: '+0.23%', price: '1.0903', positive: true },
                  { symbol: 'GBPUSD', name: 'British Pound / US Dollar', change: '-0.15%', price: '1.2654', positive: false },
                  { symbol: 'AAPL', name: 'Apple Inc', change: '+1.45%', price: '$189.43', positive: true },
                  { symbol: 'BTCUSD', name: 'Bitcoin / US Dollar', change: '+2.34%', price: '$42,156', positive: true },
                ].map((item) => (
                  <div key={item.symbol} className="flex items-center justify-between p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <div>
                      <div className="font-medium text-foreground">{item.symbol}</div>
                      <div className="text-sm text-muted-foreground">{item.name}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-medium text-foreground">{item.price}</div>
                      <div className={`text-sm ${item.positive ? 'text-chart-up' : 'text-chart-down'}`}>
                        {item.change}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <button className="btn btn-secondary btn-sm w-full mt-4">
                Manage Watchlist
              </button>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="card">
            <div className="card-header">
              <h3 className="card-title">Recent Activity</h3>
              <p className="card-description">
                Your latest trading activities
              </p>
            </div>
            <div className="card-content">
              <div className="space-y-3">
                {[
                  { action: 'Viewed EURUSD chart', time: '2 minutes ago', icon: '👁️' },
                  { action: 'Added AAPL to watchlist', time: '1 hour ago', icon: '➕' },
                  { action: 'Saved chart layout "Forex Setup"', time: '3 hours ago', icon: '💾' },
                  { action: 'Applied RSI indicator to BTCUSD', time: '5 hours ago', icon: '📊' },
                  { action: 'Logged in from new device', time: '1 day ago', icon: '🔐' },
                ].map((activity, index) => (
                  <div key={index} className="flex items-center space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-colors">
                    <div className="text-lg">{activity.icon}</div>
                    <div className="flex-1">
                      <div className="text-sm font-medium text-foreground">{activity.action}</div>
                      <div className="text-xs text-muted-foreground">{activity.time}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="mt-8">
          <h2 className="text-xl font-semibold text-foreground mb-4">Quick Actions</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <a href="/charts" className="card p-4 hover:bg-muted/50 transition-colors text-center">
              <div className="text-2xl mb-2">📈</div>
              <div className="text-sm font-medium text-foreground">View Charts</div>
            </a>
            <button className="card p-4 hover:bg-muted/50 transition-colors text-center">
              <div className="text-2xl mb-2">⚙️</div>
              <div className="text-sm font-medium text-foreground">Settings</div>
            </button>
            <a href="/news" className="card p-4 hover:bg-muted/50 transition-colors text-center">
              <div className="text-2xl mb-2">📰</div>
              <div className="text-sm font-medium text-foreground">News</div>
            </a>
            <button className="card p-4 hover:bg-muted/50 transition-colors text-center">
              <div className="text-2xl mb-2">📚</div>
              <div className="text-sm font-medium text-foreground">Tutorials</div>
            </button>
          </div>
        </div>

        {/* Account Info */}
        <div className="mt-8">
          <div className="card p-6">
            <h3 className="text-lg font-semibold text-foreground mb-4">Account Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-muted-foreground">Email</label>
                    <div className="font-medium text-foreground">{user?.email}</div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Account Status</label>
                    <div className="flex items-center space-x-2">
                      <span className="px-2 py-1 bg-green-100 text-green-800 text-xs font-medium rounded-full dark:bg-green-900 dark:text-green-200">
                        ACTIVE
                      </span>
                      <span className="text-xs text-muted-foreground">
                        Full access to all features
                      </span>
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Member Since</label>
                    <div className="font-medium text-foreground">
                      {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'Recently'}
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <div className="space-y-3">
                  <div>
                    <label className="text-sm text-muted-foreground">Email Verification</label>
                    <div className="flex items-center space-x-2">
                      {user?.emailVerified ? (
                        <span className="text-green-600 text-sm">✓ Verified</span>
                      ) : (
                        <span className="text-amber-600 text-sm">⚠ Pending verification</span>
                      )}
                    </div>
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground">Two-Factor Auth</label>
                    <div className="text-muted-foreground text-sm">Not enabled</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="mt-6 flex space-x-3">
              <button className="btn btn-secondary btn-sm">
                Edit Profile
              </button>
              <button className="btn btn-secondary btn-sm">
                Security Settings
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}